import * as Dialog from '@radix-ui/react-dialog';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/atoms/button';
import { AmbiguousWordRule, EntryMode } from './interface';

// ─── Mode Toggle (page-level) ───────────────────────────────────────────────────

export function ModeToggle({ mode, onChange }: { mode: EntryMode; onChange: (m: EntryMode) => void }) {
  return (
    <div className="flex items-center gap-1 bg-muted p-1 rounded-lg w-fit">
      <button
        type="button"
        onClick={() => onChange('quick')}
        className={cn(
          'px-3 py-1.5 rounded-md text-xs font-medium transition-all',
          mode === 'quick' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground',
        )}
      >
        Quick Hours
      </button>
      <button
        type="button"
        onClick={() => onChange('range')}
        className={cn(
          'px-3 py-1.5 rounded-md text-xs font-medium transition-all',
          mode === 'range' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground',
        )}
      >
        From / To
      </button>
    </div>
  );
}

// ─── Ambiguous-word confirmation dialog ─────────────────────────────────────
// Fires once per submission attempt (not once per flagged cell) — if any note
// in the batch matches an AMBIGUOUS_WORD_RULES entry (see utils.ts) on a
// project entry, we ask a single question up front rather than
// interrupting the user row by row. `rule` is null when nothing is pending.

export function WordConfirmDialog({
  rule,
  onConfirmLegitimate,
  onConfirmPersonal,
}: {
  rule: AmbiguousWordRule | null;
  onConfirmLegitimate: () => void;
  onConfirmPersonal: () => void;
}) {
  return (
    <Dialog.Root open={!!rule}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-card p-6 shadow-lg"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <Dialog.Title className="text-base font-semibold">Confirm entry type</Dialog.Title>
          <Dialog.Description className="mt-2 text-sm text-muted-foreground">
            {rule?.question}
          </Dialog.Description>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" onClick={onConfirmPersonal}>
              {rule?.personalLabel}
            </Button>
            <Button onClick={onConfirmLegitimate}>{rule?.legitimateLabel}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
